import { Paper, Typography } from '@material-ui/core';
import React from 'react';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import PropTypes from 'prop-types';
import { ClientExperienceNoteOverride } from 'Resources/Note';
import MetaFormInline
  from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import GoalSubsectionFieldlist
  from 'Components/Universal/Questionnaire/Sections/GoalSubsectionFieldlist';
import { goalResourcesToRecords, removeGoal, saveGoal } from 'Helpers/Goals';
import fr from 'facet-rest';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import {
  advisorHouseholdUri, householdTimelineUri,
} from 'Consts/advisorUris';
import { Link } from 'react-router-dom';
import UIButton from 'Components/UI/Button';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { Accomplishments } from 'Components/ComponentLibrary/Components/Uncategorized/Accomplishments';
import GoalSubsection from './GoalSubsection';

function ClientExperienceAccordionContent({ classes, resource }) {
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();
  const [noteResource, setNoteResource] = React.useState();
  const { rootNodeProps } = useEventTrackingLogEvent({
    nodeName: 'Accomplishments',
  });
  const [disengagedClientExperienceHousehold, setDisengagedClientExperienceHousehold] = React.useState();

  React.useEffect(() => {
    async function fetchNote() {
      const link = resource.relationship(fr.relationships.renewalNote.name).link('related');
      const collection = await link.get(
        { metaOverrideSet: ClientExperienceNoteOverride },
      );

      if (collection.resources().list().length === 0) {
        collection.create({
          addToCollection: true,
          metaOverrideSet: ClientExperienceNoteOverride,
        });
      }
      const [nextNote] = collection;
      nextNote.attr('noteType', 'renewal');
      setNoteResource(nextNote);
    }

    if (resource) {
      fetchNote().catch(e => displayErrorMessage(e, 'Failed to fetch note'));
    }
    setDisengagedClientExperienceHousehold(resource);
  }, [resource?.id()]);

  const handleUpdate = async (attr, value) => {
    try {
      resource.attr(attr, value);
      const [updatedResource] = await resource.submit();
      setDisengagedClientExperienceHousehold(updatedResource);
      displaySuccess('Successfully updated disengaged status');
    } catch (err) {
      displayErrorMessage(err, 'An error occurred while updating disengaged status');
    }
  };

  return (
    <div className={classes.root} {...rootNodeProps}>
      <Accomplishments
        isFutureAccomplishment={false}
        bestNextStepLink={resource.relationship(fr.relationships.accomplishmentBestNextSteps.name).link('related')}
        accomplishmentsLink={resource.relationship(fr.relationships.accomplishments.name).link('related')}
        title="ACCOMPLISHMENTS-"
        text="What did we accomplish with this client in the past year?"
      />
      <Paper className={classes.goals}>
        <div className={classes.title}>
          <Typography variant="h3">
            {'GOALS'}
          </Typography>
        </div>
        <GoalSubsectionFieldlist
          householdId={Number(resource.id())}
          RecordComponent={GoalSubsection}
          loadedGoals={goalResourcesToRecords([...resource.attr('goals')])}
          saveGoal={goal => saveGoal(goal,
            goals => resource.attr('goals', goals), () => null)}
          removeGoal={goalID => removeGoal(goalID,
            [...resource.attr('goals')])}
          planPrompt="View Plan"
          addGoal={() => null}
          disableAddButton
        />
      </Paper>
      <Accomplishments
        isFutureAccomplishment
        bestNextStepLink={resource.relationship(fr.relationships.nextBestNextStepBestNextSteps.name).link('related')}
        accomplishmentsLink={resource.relationship(fr.relationships.futureAccomplishments.name).link('related')}
        title="THE YEAR AHEAD-"
        text="What do we plan to accomplish in the year ahead?"
      />
      <Paper className={classes.notes}>
        <div className={classes.title}>
          <Typography variant="h3">
            {'INTERNAL NOTES'}
          </Typography>
        </div>
        {noteResource && <MetaFormInline
          className={classes.noteForm}
          resource={noteResource}
        />}
        {disengagedClientExperienceHousehold && <MetaControl
          resource={disengagedClientExperienceHousehold}
          attrName="disengaged"
          onChange={value => handleUpdate('disengaged', value)}
        />}
      </Paper>
      <div className={classes.buttonContainer}>
        <UIButton className={classes.button} variant={'contained'} color={'secondary'} component={Link} to={householdTimelineUri(resource.id())}>Timeline</UIButton>
        <UIButton className={classes.button} variant={'contained'} color={'secondary'} component={Link} to={advisorHouseholdUri(resource.id())}>Landing Page</UIButton>
      </div>
    </div>
  );
}

ClientExperienceAccordionContent.propTypes = {
  classes: PropTypes.object.isRequired,
  resource: PropTypes.object.isRequired,
};

export default withStyles(theme => ({
  root: {
    width: '100%',
    gap: '16px',
    display: 'grid',
    gridTemplate: `
    "accomplishments goals"
    "bestNextSteps notes"
    "empty button"
    `,
    gridTemplateColumns: '6fr 4fr',
    gridTemplateRows: '1fr 1fr 40px',
  },
  goals: {
    padding: theme.spacing(2),
    borderRadius: theme.radius.sm,
  },
  notes: {
    padding: theme.spacing(2),
    borderRadius: theme.radius.sm,
  },
  noteForm: {
    flexGrow: '1',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    '& > h3': {
      fontWeight: theme.typography.fontWeightBold,
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gridArea: 'button',
  },
  button: {
    maxWidth: theme.dimensions.sm,
    marginLeft: theme.spacing(1),
  },
}))(ClientExperienceAccordionContent);
